/**
 * TILE PRE-WARM (L2-09): the parent tiles a long select flight passes through, loaded while the visitor
 * reads the home view, so the first flight over the region shows land, not a blank basemap with the
 * metro box's straight edge.
 *
 *   prewarmTiles(map, { sources: ["basemap", "dem"], zooms: [9, 10], bbox: REGION_BBOX, signal })
 *
 * MapLibre loads tiles only for the camera it shows and has no public prefetch, so this uses its tile
 * manager the way a camera would: each tile is created and loaded through the source, then placed in the
 * source's out-of-view cache, where the next camera that needs it (or needs it as a parent while its
 * children load) picks it up instead of starting a load. The internals are feature-checked: on a MapLibre
 * that lacks them this does nothing, and the map behaves as before. Tiles already in view or cached,
 * or outside the source's bounds, are skipped. At most two load at a time.
 */
import type { Map as MLMap } from "maplibre-gl";
import { tilesInBox } from "@/map/memorySource";

interface TileIdLike {
  key: string;
  overscaleFactor(): number;
}
interface TileLike {
  tileID: TileIdLike;
  hasData(): boolean;
  getExpiryTimeout(): number | undefined;
}
interface TileManagerLike {
  _source: { tileSize: number; hasTile?: (id: TileIdLike) => boolean; minzoom?: number; maxzoom?: number };
  _inViewTiles: { getAllTiles(): TileLike[]; getTileById(key: string): TileLike | undefined };
  _outOfViewCache: { add(id: TileIdLike, tile: TileLike, expiry?: number): void; has(id: TileIdLike): boolean };
  _loadTile(tile: TileLike, key: string, state: string, hadData: boolean): Promise<void>;
}
type TileCtor = new (id: TileIdLike, size: number) => TileLike & { state: string };
type IdCtor = new (overscaledZ: number, wrap: number, z: number, x: number, y: number) => TileIdLike;

function managerOf(map: MLMap, id: string): TileManagerLike | null {
  const style = (map as unknown as { style?: { tileManagers?: Record<string, unknown>; sourceCaches?: Record<string, unknown> } }).style;
  const tm = (style?.tileManagers?.[id] ?? style?.sourceCaches?.[id]) as TileManagerLike | undefined;
  if (!tm || typeof tm._loadTile !== "function" || !tm._outOfViewCache?.add || !tm._inViewTiles?.getAllTiles || !tm._source) return null;
  return tm;
}

/** Loads (z, x, y) tiles of each source into its out-of-view cache. Resolves with the number loaded. */
export async function prewarmTiles(
  map: MLMap,
  o: { sources: string[]; zooms: number[]; bbox: readonly [number, number, number, number]; signal?: AbortSignal; concurrency?: number },
): Promise<number> {
  let loaded = 0;
  for (const sid of o.sources) {
    if (o.signal?.aborted) break;
    let tm: TileManagerLike | null = null;
    try {
      tm = managerOf(map, sid);
    } catch {
      tm = null;
    }
    const sample = tm?._inViewTiles.getAllTiles()[0];
    if (!tm || !sample) continue; // nothing in view yet to learn the tile classes from
    const Tile = sample.constructor as unknown as TileCtor;
    const Id = sample.tileID.constructor as unknown as IdCtor;
    const src = tm._source;
    const queue: TileIdLike[] = [];
    for (const z of o.zooms) {
      if ((src.minzoom !== undefined && z < src.minzoom) || (src.maxzoom !== undefined && z > src.maxzoom)) continue;
      for (const [tz, x, y] of tilesInBox(o.bbox, z)) {
        let id: TileIdLike;
        try {
          id = new Id(tz, 0, tz, x, y);
        } catch {
          return loaded;
        }
        if (src.hasTile && !src.hasTile(id)) continue;
        if (tm._inViewTiles.getTileById(id.key) || tm._outOfViewCache.has(id)) continue;
        queue.push(id);
      }
    }
    const worker = async (): Promise<void> => {
      while (queue.length && !o.signal?.aborted) {
        const id = queue.shift()!;
        if (tm!._inViewTiles.getTileById(id.key) || tm!._outOfViewCache.has(id)) continue;
        try {
          const tile = new Tile(id, src.tileSize * id.overscaleFactor());
          await tm!._loadTile(tile, id.key, tile.state, false);
          // a camera may have asked for it meanwhile: then it is that camera's tile
          if (tile.hasData() && !tm!._inViewTiles.getTileById(id.key) && !tm!._outOfViewCache.has(id)) {
            tm!._outOfViewCache.add(id, tile, tile.getExpiryTimeout());
            loaded++;
          }
        } catch {
          /* a tile that fails now loads on demand */
        }
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, o.concurrency ?? 2) }, worker));
  }
  return loaded;
}
